import createClient from 'openapi-fetch';
import { AppError, errorFromResponse } from './errors';
import type { paths } from './schema.gen';

// The browser session (ADR 0008): the refresh token is an HttpOnly cookie the page never sees, and
// the access token lives only in this module's memory. A reload asks the server for a new one.

let accessToken: string | null = null;
let refreshing: Promise<RefreshOutcome> | null = null;
const expiredListeners = new Set<() => void>();

/** Where requests go: the network, or the public demo's server inside the page (`/demo`). Looked up on
 * every call, so tests that stub the global fetch keep working. */
let transport: typeof fetch = (input, init) => fetch(input, init);

/** Sends every request through `f` instead of the network. Only the demo does. */
export function setTransport(f: typeof fetch): void {
  transport = f;
}

/** How asking for a new access token went: got one, the session is over, or no answer. */
export type RefreshOutcome = 'ok' | 'rejected' | 'offline';

/** Called when the server ends the session (the cookie was rejected), so the app shows sign-in. */
export function onSessionExpired(listener: () => void): () => void {
  expiredListeners.add(listener);
  return () => expiredListeners.delete(listener);
}

const isSessionPath = (url: string) => new URL(url, location.origin).pathname.startsWith('/v1/auth/');

/** Exchanges the session cookie for an access token, once, however many requests wait on it. */
export function refreshAccessToken(): Promise<RefreshOutcome> {
  refreshing ??= (async (): Promise<RefreshOutcome> => {
    try {
      const res = await transport('/v1/auth/session/refresh', { method: 'POST', credentials: 'same-origin' });
      if (res.ok) {
        accessToken = ((await res.json()) as { access_token: string }).access_token;
        return 'ok';
      }
      // A rejected cookie ends the session; a server hiccup does not.
      if (res.status === 401) {
        const hadSession = accessToken !== null;
        accessToken = null;
        if (hadSession) expiredListeners.forEach((l) => l());
        return 'rejected';
      }
      return 'offline';
    } catch {
      return 'offline';
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

function withToken(req: Request): Request {
  if (!accessToken || isSessionPath(req.url)) return req;
  const headers = new Headers(req.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);
  return new Request(req, { headers });
}

/** Sends a request with the access token; on 401 refreshes once and tries again. */
async function sessionFetch(req: Request): Promise<Response> {
  const retry = req.clone();
  const sent = accessToken;
  const res = await transport(withToken(req));
  if (res.status !== 401 || isSessionPath(req.url)) return res;
  // Another request may already have refreshed while this one was in flight.
  const refreshed = (accessToken !== null && accessToken !== sent) || (await refreshAccessToken()) === 'ok';
  return refreshed ? transport(withToken(retry)) : res;
}

export const api = createClient<paths>({ baseUrl: location.origin, fetch: sessionFetch });

/** The data of an openapi-fetch result, or its failure as an AppError. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) {
    throw errorFromResponse(result.response.status, result.error);
  }
  return result.data as T;
}

/** A fresh Idempotency-Key, so a retried click never decides twice. */
export function idempotencyKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return `web-${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
}

// ---- Sign-in and sign-out ----

export async function startSession(email: string, password: string): Promise<void> {
  const res = await transport('/v1/auth/session', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim(), password }),
  });
  const body: unknown = await res.json().catch(() => undefined);
  if (!res.ok) throw errorFromResponse(res.status, body);
  accessToken = (body as { access_token: string }).access_token;
}

export async function endSession(): Promise<void> {
  accessToken = null;
  await transport('/v1/auth/session', { method: 'DELETE', credentials: 'same-origin' }).catch(() => undefined);
}

export function hasAccessToken(): boolean {
  return accessToken !== null;
}

/** For tests. */
export function resetSessionForTests(token: string | null = null): void {
  accessToken = token;
  refreshing = null;
  expiredListeners.clear();
}

export { AppError };
